import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import "./sales.css";

const UNITS = [
  "Sq. Ft.",
  "Sq. M.",
  "Running Ft.",
  "Running M.",
  "Acre",
  "Yard",
  "Nos.",
  "Hours",
  "Days",
  "Lump Sum",
  "Percentage",
];

const TERM_SERVICE_TYPES = ["Architecture", "Interior", "Structure"];

const NUMBER_FIELDS = ["defaultRate", "gstRate", "paymentTermsDays"];

const FormField = ({
  name,
  placeholder,
  type = "text",
  value,
  onChange,
  required = false,
  className = "",
  children,
}) => {
  if (children) {
    return (
      <select
        className={className}
        name={name}
        value={value ?? ""}
        onChange={(e) => onChange(name, e.target.value)}
        required={required}
      >
        {children}
      </select>
    );
  }

  return (
    <input
      className={className}
      name={name}
      type={type}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={(e) => onChange(name, e.target.value)}
      required={required}
    />
  );
};

/* ============================================================
   MAIN COMPONENT
============================================================ */

export default function SalesMasterPage({ type }) {
  const config = useMemo(
    () =>
      ({
        services: {
          title: "Service Master",
          endpoint: "/sales/services",
        },

        clients: {
          title: "Client Master",
          endpoint: "/sales/clients",
        },

        banks: {
          title: "Bank Detail Master",
          endpoint: "/sales/banks",
        },

        terms: {
          title: "Terms & Conditions Master",
          endpoint: "/sales/terms",
        },
      })[type],
    [type],
  );

  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [unitFilter, setUnitFilter] = useState("");

  const token = localStorage.getItem("token");

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  /* ============================================================
     LOAD DATA
  ============================================================ */

  const load = async () => {
    try {
      setLoading(true);

      const { data } = await api.get(config.endpoint, {
        headers,
      });

      setRows(data || []);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     TYPE CHANGE
  ============================================================ */

  useEffect(() => {
    setEditing(null);
    setForm({});
    load();
  }, [type]);

  /* ============================================================
     HANDLE CHANGE
     Keep values as STRING while typing.
     This prevents cursor/focus issues.
  ============================================================ */

  const handleChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ============================================================
     SAVE
  ============================================================ */

  const save = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...form,
      };

      /* Convert number fields only when saving */
      NUMBER_FIELDS.forEach((field) => {
        if (payload[field] !== undefined && payload[field] !== "") {
          payload[field] = Number(payload[field]);
        }
      });

      /* Terms textarea -> array */
      if (type === "terms") {
        payload.terms = Array.isArray(payload.terms)
          ? payload.terms.map((item) => String(item).trim()).filter(Boolean)
          : String(payload.terms || "")
              .split("\n")
              .map((item) => item.trim())
              .filter(Boolean);
      }

      /* Update */
      if (editing) {
        await api.put(`${config.endpoint}/${editing}`, payload, {
          headers,
        });
      } else {
        /* Create */
        await api.post(config.endpoint, payload, {
          headers,
        });
      }

      setEditing(null);
      setForm({});

      await load();
    } catch (error) {
      console.error("Failed to save:", error);
    }
  };

  /* ============================================================
     EDIT
  ============================================================ */

  const edit = (row) => {
    const copy = {
      ...row,
    };

    /* Terms */
    if (type === "terms") {
      copy.service = row.service || "";
      copy.terms = Array.isArray(row.terms) ? [...row.terms] : [];
    }

    /* Convert numbers to strings for inputs */
    NUMBER_FIELDS.forEach((field) => {
      if (copy[field] !== undefined && copy[field] !== null) {
        copy[field] = String(copy[field]);
      }
    });

    setEditing(row._id);
    setForm(copy);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ============================================================
     DELETE
  ============================================================ */

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) {
      return;
    }

    try {
      await api.delete(`${config.endpoint}/${id}`, {
        headers,
      });

      await load();
    } catch (error) {
      console.error("Failed to delete:", error);
    }
  };

  /* ============================================================
     CANCEL
  ============================================================ */

  const cancelEdit = () => {
    setEditing(null);
    setForm({});
  };

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return rows.filter((row) => {
      const searchable = [
        row.name,
        row.description,
        row.code,
        row.unit,
        row.sacCode,
        row.gstRate,
        row.companyName,
        row.contactPerson,
        row.email,
        row.phone,
        row.gstin,
        row.pan,
        row.currency,
        row.bankName,
        row.accountName,
        row.accountNumber,
        row.ifsc,
        row.swiftCode,
        row.branch,
        row.upiId,
        row.company,
        row.service,
        row.title,
        ...(Array.isArray(row.terms) ? row.terms : []),
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !q || searchable.includes(q);

      const matchesUnit =
        type !== "services" ||
        !unitFilter ||
        String(row.unit || "") === unitFilter;

      return matchesSearch && matchesUnit;
    });
  }, [rows, searchText, unitFilter, type]);

  const clearSearch = () => {
    setSearchText("");
    setUnitFilter("");
  };

  /* ============================================================
     RETURN
  ============================================================ */

  return (
    <div className="sales-page">
      {/* ========================================================
         HEADER
      ======================================================== */}

      <div className="sales-head">
        <div>
          <div className="sales-eyebrow">FINANCE • SALES MASTER</div>
          <h2>{config.title}</h2>
          <p>Create, edit and manage sales masters.</p>
        </div>

        <div className="sales-head-meta">
          <span>{rows.length} Total</span>
          <span>{filteredRows.length} Showing</span>
        </div>
      </div>

      {/* ========================================================
         FORM
      ======================================================== */}

      <form
        className={`sales-card sales-form sales-form-${type}`}
        onSubmit={save}
      >
        <div className="sales-form-title">
          <div>
            <h3>{editing ? `Edit ${config.title}` : `Add ${config.title}`}</h3>
            <p>
              {type === "services"
                ? "Create a reusable service with pricing, unit and tax details."
                : type === "clients"
                  ? "Maintain client billing and commercial details."
                  : type === "banks"
                    ? "Maintain company bank account details."
                    : "Maintain service-wise terms and conditions."}
            </p>
          </div>

          {editing && (
            <span className="sales-editing-badge">Editing Record</span>
          )}
        </div>
        {/* ======================================================
           SERVICES
        ====================================================== */}

        {type === "services" && (
          <>
            <FormField
              className="service-field service-name-field"
              name="name"
              placeholder="Service Name"
              value={form.name}
              onChange={handleChange}
              required
            />

            <textarea
              className="service-field service-description-field"
              name="description"
              placeholder="Service Description"
              value={form.description ?? ""}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
            />

            <FormField
              className="service-field service-code-field"
              name="code"
              placeholder="Service Code"
              value={form.code}
              onChange={handleChange}
            />

            <FormField
              className="service-field service-unit-field"
              name="unit"
              value={form.unit}
              onChange={handleChange}
            >
              <option value="">Unit</option>

              {UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </FormField>

            <FormField
              className="service-field service-rate-field"
              name="defaultRate"
              placeholder="Default Rate"
              type="number"
              value={form.defaultRate}
              onChange={handleChange}
            />

            <FormField
              className="service-field service-sac-field"
              name="sacCode"
              placeholder="SAC Code"
              value={form.sacCode}
              onChange={handleChange}
            />

            <FormField
              className="service-field service-gst-field"
              name="gstRate"
              placeholder="GST %"
              type="number"
              value={form.gstRate}
              onChange={handleChange}
            />
          </>
        )}

        {/* ======================================================
           CLIENTS
        ====================================================== */}

        {type === "clients" && (
          <>
            <FormField
              name="companyName"
              placeholder="Client / Company Name"
              value={form.companyName}
              onChange={handleChange}
              required
            />

            <FormField
              name="contactPerson"
              placeholder="Contact Person"
              value={form.contactPerson}
              onChange={handleChange}
            />

            <FormField
              name="email"
              placeholder="Email"
              type="email"
              value={form.email}
              onChange={handleChange}
            />

            <FormField
              name="phone"
              placeholder="Phone"
              value={form.phone}
              onChange={handleChange}
            />

            <FormField
              name="gstin"
              placeholder="GSTIN"
              value={form.gstin}
              onChange={handleChange}
            />

            <FormField
              name="pan"
              placeholder="PAN"
              value={form.pan}
              onChange={handleChange}
            />

            <FormField
              name="currency"
              placeholder="Currency"
              value={form.currency}
              onChange={handleChange}
            />

            <FormField
              name="paymentTermsDays"
              placeholder="Payment Terms Days"
              type="number"
              value={form.paymentTermsDays}
              onChange={handleChange}
            />

            <textarea
              name="billingAddress"
              placeholder="Billing Address"
              value={form.billingAddress?.address ?? ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,

                  billingAddress: {
                    ...(prev.billingAddress || {}),
                    address: e.target.value,
                  },
                }))
              }
            />
          </>
        )}

        {/* ======================================================
           BANKS
        ====================================================== */}

        {type === "banks" && (
          <>
            <FormField
              name="company"
              value={form.company}
              onChange={handleChange}
              required
            >
              <option value="">Select Company</option>

              <option value="SDC">Sheth Design Consultans LLP </option>

              <option value="SVB">Softvolute Buildcon LLP</option>

              <option value="Magnira">Magnira Universe LLP</option>
            </FormField>

            <FormField
              name="bankName"
              placeholder="Bank Name"
              value={form.bankName}
              onChange={handleChange}
              required
            />

            <FormField
              name="accountName"
              placeholder="Account Name"
              value={form.accountName}
              onChange={handleChange}
            />

            <FormField
              name="accountNumber"
              placeholder="Account Number"
              value={form.accountNumber}
              onChange={handleChange}
            />

            <FormField
              name="ifsc"
              placeholder="IFSC"
              value={form.ifsc}
              onChange={handleChange}
            />

            <FormField
              name="swiftCode"
              placeholder="SWIFT Code"
              value={form.swiftCode}
              onChange={handleChange}
            />

            <FormField
              name="branch"
              placeholder="Branch"
              value={form.branch}
              onChange={handleChange}
            />

            <FormField
              name="upiId"
              placeholder="UPI ID"
              value={form.upiId}
              onChange={handleChange}
            />
          </>
        )}

        {/* ======================================================
           TERMS
        ====================================================== */}

        {type === "terms" && (
          <>
            <FormField
              name="service"
              value={form.service}
              onChange={handleChange}
              required
            >
              <option value="">Select Service Type</option>

              {TERM_SERVICE_TYPES.map((serviceType) => (
                <option key={serviceType} value={serviceType}>
                  {serviceType}
                </option>
              ))}
            </FormField>

            <FormField
              name="title"
              placeholder="Title"
              value={form.title}
              onChange={handleChange}
            />

            <div className="sales-terms-editor">
              {(Array.isArray(form.terms)
                ? form.terms
                : String(form.terms || "")
                    .split("\n")
                    .filter(Boolean)
              ).map((term, index) => (
                <div className="sales-term-row" key={index}>
                  <span className="sales-term-number">{index + 1}.</span>

                  <input
                    type="text"
                    value={term}
                    placeholder={`Term ${index + 1}`}
                    onChange={(e) => {
                      const terms = Array.isArray(form.terms)
                        ? [...form.terms]
                        : String(form.terms || "")
                            .split("\n")
                            .filter(Boolean);

                      terms[index] = e.target.value;

                      setForm((prev) => ({
                        ...prev,
                        terms,
                      }));
                    }}
                  />

                  <button
                    type="button"
                    className="sales-term-remove"
                    onClick={() => {
                      const terms = Array.isArray(form.terms)
                        ? [...form.terms]
                        : String(form.terms || "")
                            .split("\n")
                            .filter(Boolean);

                      terms.splice(index, 1);

                      setForm((prev) => ({
                        ...prev,
                        terms,
                      }));
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="sales-add-term"
                onClick={() => {
                  const terms = Array.isArray(form.terms)
                    ? [...form.terms]
                    : String(form.terms || "")
                        .split("\n")
                        .filter(Boolean);

                  setForm((prev) => ({
                    ...prev,
                    terms: [...terms, ""],
                  }));
                }}
              >
                + Add Term
              </button>
            </div>
          </>
        )}

        {/* ======================================================
           ACTIONS
        ====================================================== */}

        <div className="sales-form-actions">
          <button
            type="submit"
            className="sales-btn primary"
            disabled={loading}
          >
            {editing
              ? type === "services"
                ? "Update Service"
                : "Update"
              : type === "services"
                ? "Save Service"
                : "Save"}
          </button>

          {editing && (
            <button type="button" className="sales-btn" onClick={cancelEdit}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* ========================================================
         TABLE
      ======================================================== */}

      <div className="sales-list-toolbar sales-card">
        <div className="sales-search-wrap">
          <span className="sales-search-icon">⌕</span>

          <input
            type="text"
            className="sales-search-input"
            value={searchText}
            placeholder={
              type === "services"
                ? "Search service, description, code, SAC, unit..."
                : type === "clients"
                  ? "Search by client, contact, email, phone..."
                  : type === "banks"
                    ? "Search by bank, account, IFSC, company..."
                    : "Search terms & conditions..."
            }
            onChange={(e) => setSearchText(e.target.value)}
          />

          {searchText && (
            <button
              type="button"
              className="sales-search-clear"
              onClick={() => setSearchText("")}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {type === "services" && (
          <select
            className="sales-filter-select"
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
          >
            <option value="">All Units</option>
            {UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        )}

        {(searchText || unitFilter) && (
          <button
            type="button"
            className="sales-btn sales-clear-filter"
            onClick={clearSearch}
          >
            Clear
          </button>
        )}

        <div className="sales-list-count">
          <strong>{filteredRows.length}</strong>
          <span>records</span>
        </div>
      </div>

      <div className="sales-card sales-table-wrap">
        <table className="sales-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{type === "services" ? "Service" : "Name / Detail"}</th>
              {type === "services" && <th>Description</th>}
              <th>{type === "services" ? "Commercial Details" : "Extra"}</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredRows.map((row, index) => (
              <tr key={row._id}>
                <td>{index + 1}</td>

                <td>
                  {type === "services" ? (
                    <div className="sales-service-name-cell">
                      <strong>{row.name || "-"}</strong>
                      <span>
                        {row.code ? `Code: ${row.code}` : "No service code"}
                      </span>
                    </div>
                  ) : (
                    row.name ||
                    row.companyName ||
                    row.bankName ||
                    row.title ||
                    "-"
                  )}
                </td>

                {type === "services" && (
                  <td className="sales-service-description-cell">
                    {row.description ? (
                      <span>{row.description}</span>
                    ) : (
                      <span className="sales-muted">No description</span>
                    )}
                  </td>
                )}

                <td>
                  {type === "services" ? (
                    <div className="sales-service-meta">
                      <span className="sales-meta-pill">
                        {row.unit || "No Unit"}
                      </span>
                      {row.sacCode && (
                        <span className="sales-meta-pill">
                          SAC {row.sacCode}
                        </span>
                      )}
                      <span className="sales-meta-pill">
                        GST {Number(row.gstRate || 0)}%
                      </span>
                      <span className="sales-meta-rate">
                        ₹{" "}
                        {Number(row.defaultRate || 0).toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  ) : (
                    row.unit ||
                    row.contactPerson ||
                    row.company ||
                    row.service ||
                    "-"
                  )}
                </td>

                <td>
                  <button
                    type="button"
                    className="sales-link"
                    onClick={() => edit(row)}
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    className="sales-link danger"
                    onClick={() => remove(row._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}

            {!filteredRows.length && (
              <tr>
                <td
                  colSpan={type === "services" ? 5 : 4}
                  className="sales-empty"
                >
                  {loading
                    ? "Loading..."
                    : rows.length
                      ? "No matching records found."
                      : "No records found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
